/**
 * v3.19-1423 COMMS-MODERN Phase 2 · Bulletin Board tab (complete).
 *
 * Features:
 *   • Search bar (server-side title+body match)
 *   • Sort newest first with pinned floating to top (server order)
 *   • Rich text body (bold, italic, code, auto-links)
 *   • Attachments (up to 6 per post, 10MB each) — reuses existing
 *     base64-blob upload pattern from company_documents.
 *   • Expiration date — expired posts hidden by default
 *   • "Edited N ago" chip when updated_at differs from created_at
 *   • Admin-only writes (pin, expire, delete, attach)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import {
  Pin, PinOff, Plus, Megaphone, Loader2, Trash2, AlertCircle,
  Search as SearchIcon, Paperclip, Download, X, Clock, Pencil,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatET } from '../utils/timeUtils';
import { renderRichText } from '../lib/richText';

const CATEGORIES = [
  { key: 'company_update',    label: 'Company Update',     color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { key: 'meeting_reminder',  label: 'Meeting Reminder',   color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  { key: 'sales_contest',     label: 'Sales Contest',      color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { key: 'hr_notice',         label: 'HR Notice',          color: 'bg-amber-100 text-amber-800 border-amber-200' },
  { key: 'training',          label: 'Training',           color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { key: 'policy_update',     label: 'Policy Update',      color: 'bg-slate-100 text-slate-800 border-slate-200' },
];
const catByKey = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const humanBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const wasEdited = (p) => {
  if (!p?.updated_at || !p?.created_at) return false;
  return new Date(p.updated_at).getTime() - new Date(p.created_at).getTime() > 2000;
};

export default function BulletinBoardTab() {
  const { user } = useAuth();
  const isAdmin = ['ptc_admin','ptc_director','company_admin','company_director'].includes((user?.role || '').toLowerCase());

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', category: 'company_update', expires_at: '' });
  const [attachingPostId, setAttachingPostId] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bulletin-posts', { params: q.trim() ? { q: q.trim() } : {} });
      setPosts(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bulletin');
    } finally {
      setLoading(false);
    }
  }, [q]);

  // Debounce search
  useEffect(() => {
    const t = window.setTimeout(load, q ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [q, load]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      await api.post('/bulletin-posts', {
        title:      form.title,
        body:       form.body,
        category:   form.category,
        expires_at: form.expires_at || null,
      });
      toast.success('Bulletin posted');
      setComposeOpen(false);
      setForm({ title: '', body: '', category: 'company_update', expires_at: '' });
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to post');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (post) => {
    try {
      await api.patch(`/bulletin-posts/${post.id}`, { pinned: !post.pinned });
      toast.success(post.pinned ? 'Unpinned' : 'Pinned to top');
      load();
    } catch { toast.error('Failed to pin'); }
  };

  const remove = async (post) => {
    if (!window.confirm('Delete this bulletin?')) return;
    try {
      await api.delete(`/bulletin-posts/${post.id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const attach = (postId) => {
    setAttachingPostId(postId);
    // Trigger native file picker on next tick so state has settled.
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };
  const onFilesPicked = async (e) => {
    const postId = attachingPostId;
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setAttachingPostId(null);
    if (!postId || !files.length) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await api.post(`/bulletin-posts/${postId}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Attached ${file.name}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to attach ${file.name}`);
      }
    }
    load();
  };

  const removeAttachment = async (postId, attachmentId) => {
    try {
      await api.delete(`/bulletin-posts/${postId}/attachments/${attachmentId}`);
      toast.success('Attachment removed');
      load();
    } catch { toast.error('Failed to remove attachment'); }
  };

  return (
    <div className="space-y-4" data-testid="bulletin-board-tab">
      {/* Hidden file input reused for every "Attach" action */}
      <input
        ref={fileInputRef} type="file" multiple hidden onChange={onFilesPicked}
        data-testid="bulletin-attach-file-input"
      />

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <Megaphone className="h-5 w-5 text-emerald-700 shrink-0" />
            <CardTitle className="text-lg">Bulletin Board</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-1 justify-end">
            <div className="relative max-w-xs flex-1">
              <SearchIcon className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search bulletins…"
                className="pl-8"
                data-testid="bulletin-search-input"
              />
            </div>
            {isAdmin && (
              <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="bulletin-new-btn"><Plus className="h-4 w-4 mr-1" /> Post</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg" data-testid="bulletin-compose-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-base">New Bulletin</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={create} className="space-y-3">
                    <Input
                      autoFocus placeholder="Title" value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      data-testid="bulletin-title-input"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="bulletin-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="relative">
                        <Clock className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <Input
                          type="date"
                          value={form.expires_at}
                          onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                          className="pl-8"
                          data-testid="bulletin-expires-input"
                          title="Optional — hides post after this date"
                        />
                      </div>
                    </div>
                    <Textarea
                      rows={7}
                      placeholder="Body — supports **bold**, *italic*, `code`, line breaks, and http/https links auto-link."
                      value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      data-testid="bulletin-body-input"
                    />
                    <div className="text-[11px] text-slate-500">
                      Attachments (up to 6, 10MB each) can be added AFTER posting from the post&apos;s paperclip button.
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => setComposeOpen(false)}>Cancel</Button>
                      <Button type="submit" size="sm" disabled={!form.title.trim() || !form.body.trim() || saving} data-testid="bulletin-submit">
                        {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Megaphone className="h-3 w-3 mr-1" />} Post
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="bulletin-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && posts.length === 0 && (
            <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="bulletin-empty">
              <AlertCircle className="h-4 w-4" />
              {q.trim() ? `No bulletins match "${q.trim()}".` : (isAdmin ? 'No bulletins yet. Post the first one above.' : 'No bulletins yet.')}
            </div>
          )}
          {!loading && posts.length > 0 && (
            <div className="space-y-3" data-testid="bulletin-list">
              {posts.map((p) => {
                const cat = catByKey[p.category] || catByKey.company_update;
                return (
                  <article
                    key={p.id}
                    className={`rounded-md border p-3.5 ${p.pinned ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
                    data-testid={`bulletin-post-${p.id}`}
                  >
                    <header className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-semibold text-slate-900 leading-snug break-words">{p.title}</h3>
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${cat.color}`}>{cat.label}</span>
                          {p.pinned && (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold flex items-center gap-1">
                              <Pin className="h-2.5 w-2.5" /> Pinned
                            </span>
                          )}
                          {p.expires_at && (
                            <span className="text-[10px] uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded font-semibold flex items-center gap-1" title={`Expires ${p.expires_at}`}>
                              <Clock className="h-2.5 w-2.5" /> Expires {p.expires_at}
                            </span>
                          )}
                          {wasEdited(p) && (
                            <span className="text-[10px] text-slate-500 flex items-center gap-1" title={`Edited ${formatET(p.updated_at)}`}>
                              <Pencil className="h-2.5 w-2.5" /> edited
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {p.author_name || 'Unknown'} · {formatET(p.created_at)}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => attach(p.id)} title="Attach file" data-testid={`bulletin-attach-${p.id}`}>
                            <Paperclip className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => togglePin(p)} title={p.pinned ? 'Unpin' : 'Pin'}>
                            {p.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(p)} title="Delete">
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </header>
                    <div
                      className="mt-2 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed"
                      data-testid={`bulletin-body-${p.id}`}
                      dangerouslySetInnerHTML={{ __html: renderRichText(p.body) }}
                    />
                    {(p.attachments && p.attachments.length > 0) && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid={`bulletin-attachments-${p.id}`}>
                        {p.attachments.map((a) => (
                          <div
                            key={a.id}
                            className="inline-flex items-center gap-1 text-[11px] rounded border border-slate-200 bg-slate-50 px-2 py-0.5"
                          >
                            <a
                              href={`${api.defaults.baseURL}/bulletin-posts/${p.id}/attachments/${a.id}`}
                              onClick={async (ev) => {
                                // We need the auth header, so intercept and blob-download.
                                ev.preventDefault();
                                try {
                                  const res = await api.get(`/bulletin-posts/${p.id}/attachments/${a.id}`, { responseType: 'blob' });
                                  const url = URL.createObjectURL(res.data);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = a.file_name;
                                  link.click();
                                  URL.revokeObjectURL(url);
                                } catch { toast.error('Failed to download'); }
                              }}
                              className="text-indigo-700 hover:underline inline-flex items-center gap-1"
                            >
                              <Download className="h-3 w-3" />
                              {a.file_name}
                            </a>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500">{humanBytes(a.size_bytes)}</span>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => removeAttachment(p.id, a.id)}
                                title="Remove attachment"
                                className="text-slate-400 hover:text-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
